'use client'
import { NbAirportGroup } from '@/src/hooks/srp/includeAirportsManager'
import { Box, Button, Checkbox, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'

interface IncludeAirportsFilterProps {
    groups: NbAirportGroup[]
    onAirportSelect: (airportId: string) => void
    onReset: () => void
    shouldShowReset: boolean
    title: string
}

const IncludeAirportsFilter: React.FC<IncludeAirportsFilterProps> = ({
    onAirportSelect,
    onReset,
    shouldShowReset,
    title,
    groups
}) => {
    const t = useTranslations('common')
    const tFilters = useTranslations('srpFilters')
    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.900' }}>
                    {title}
                </Typography>
                {shouldShowReset && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={onReset}
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'brand.primary',
                            p: 0,
                            minWidth: 'auto',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: 'brand.primaryHover'
                            }
                        }}
                    >
                        {t('reset')}
                    </Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {groups.map(group => (
                    <Box key={group.airportCode} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: 'grey.600', fontSize: '0.78rem' }}
                        >
                            {group.groupType === 'departure'
                                ? tFilters('departureAirportFrom', {
                                    airport: `${group.airportName} (${group.airportCode})`
                                })
                                : tFilters('arrivalAirportTo', {
                                    airport: `${group.airportName} (${group.airportCode})`
                                })}
                        </Typography>
                        {group.airports.map(airport => (
                            <Box
                                key={airport.value}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    '&:hover .airline-only-text': {
                                        opacity: 1
                                    }
                                }}
                                onClick={() => onAirportSelect(airport.value)}
                            >
                                <Checkbox
                                    checked={!!airport.isSelected}
                                    sx={{
                                        color: 'grey.400',
                                        '&.Mui-checked': {
                                            color: 'brand.primary'
                                        },
                                        p: 0.5
                                    }}
                                />
                                <Box
                                    sx={{
                                        ml: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flex: 1
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ position: 'relative' }}>
                                            <Tooltip title={`${airport.value} ${airport.label}`} followCursor>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        maxWidth: 220,
                                                        overflow: 'hidden',
                                                        pr: 1.5
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 700, color: 'grey.900', flexShrink: 0 }}
                                                    >
                                                        {airport.value}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#333333',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {airport.label}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export { IncludeAirportsFilter }
export type { IncludeAirportsFilterProps }
